import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { FikoGrup } from '../FikoGrup';
import { FikoSahne } from '../FikoSahne';

// ============================================================================
// TİPLER VE ARAYÜZLER
// ============================================================================

/** Element durumu */
export type ElementDurumu = 'normal' | 'secili' | 'vurgulu' | 'deaktif' | 'hata';

/** Transform kilidi */
export interface ITransformKilit {
    konum: boolean;
    rotasyon: boolean;
    olcek: boolean;
}

/** Element olayları */
export interface IElementOlaylari {
    secildi?: () => void;
    secimKaldirildi?: () => void;
    vurgulandi?: () => void;
    vurguKaldirildi?: () => void;
    tiklandi?: () => void;
    ciftTiklandi?: () => void;
    sagTiklandi?: () => void;
    suruklemeBasladi?: () => void;
    suruklemeBitti?: () => void;
    degisti?: () => void;
}

/**
 * FikoYT 3D Sisteminin Kök Ata Sınıfı
 *
 * Bu sınıf ürün ağacında ve çizimlerde gözüken tüm 3D elementlerin
 * temel ata sınıfıdır. Yeni 3D elementler bu sınıftan miras almalıdır.
 *
 * @extends FikoGrup
 */
export class FikoytKok3DElement extends FikoGrup {
    override type = 'FikoytKok3DElement';

    // ========================================================================
    // KİMLİK VE ETİKET
    // ========================================================================

    /** Element benzersiz kimliği */
    elementId: string;

    /** Element etiketi (ürün ağacında gösterilecek) */
    etiket: string = '';

    /** Element açıklaması */
    aciklama: string = '';

    // ========================================================================
    // SİMGE (GETTER/SETTER)
    // ========================================================================

    /** Simge yolu */
    get simge(): string {
        return this.userData['FikoArayuz']?.simge || '';
    }
    set simge(deger: string) {
        if (this.userData['FikoArayuz']) {
            this.userData['FikoArayuz'].simge = deger;
        }
    }

    // ========================================================================
    // SEÇİM VE RAYCAST
    // ========================================================================

    /** Raycast ile seçilebilir mi */
    private _secilebilir: boolean = true;
    get secilebilir(): boolean { return this._secilebilir; }
    set secilebilir(deger: boolean) {
        this._secilebilir = deger;
        this.userData['secilebilir'] = deger;
        // Alt child'lara da uygula
        this.traverse((child) => {
            child.userData['secilebilir'] = deger;
        });
    }

    /** Raycast'e dahil olup olmadığı */
    private _raycastAktif: boolean = true;
    private _originalRaycast: ((raycaster: THREE.Raycaster, intersects: THREE.Intersection[]) => void) | null = null;

    get raycastAktif(): boolean { return this._raycastAktif; }
    set raycastAktif(deger: boolean) {
        this._raycastAktif = deger;
        if (!deger) {
            // Raycast'i devre dışı bırak
            if (!this._originalRaycast) {
                this._originalRaycast = this.raycast.bind(this);
            }
            this.raycast = () => {};
        } else {
            // Raycast'i geri aç
            if (this._originalRaycast) {
                this.raycast = this._originalRaycast;
            }
        }
    }

    /** Şu an seçili mi */
    secili: boolean = false;

    /** Şu an vurgulu (hover) mu */
    vurgulu: boolean = false;

    /** Odakta mı */
    odakAktif: boolean = false;

    /** Element durumu */
    durum: ElementDurumu = 'normal';

    // ========================================================================
    // SABİT BOYUT (KAMERA BAĞIMSIZ)
    // ========================================================================

    /** Kameraya göre sabit boyut (zoom'dan etkilenmez) */
    private _sabitBoyut: boolean = false;
    get sabitBoyut(): boolean { return this._sabitBoyut; }
    set sabitBoyut(deger: boolean) {
        this._sabitBoyut = deger;
        if (deger) {
            // İlk scale'i kaydet
            if (!this.userData['originalScale']) {
                this.userData['originalScale'] = this.scale.clone();
            }
        }
    }

    /** Sabit boyut için referans mesafe */
    sabitBoyutMesafe: number = 10;

    /** Sabit boyut çarpanı */
    sabitBoyutCarpan: number = 1;

    // ========================================================================
    // KİLİT VE EDİT
    // ========================================================================

    /** Element tamamen kilitli mi (hiçbir değişiklik yapılamaz) */
    private _kilitli: boolean = false;
    get kilitli(): boolean { return this._kilitli; }
    set kilitli(deger: boolean) {
        this._kilitli = deger;
        if (deger) {
            this.transformKilit = { konum: true, rotasyon: true, olcek: true };
        }
    }

    /** Transform kilitleri (ayrı ayrı kilitlenebilir) */
    private _transformKilit: ITransformKilit = {
        konum: false,
        rotasyon: false,
        olcek: false
    };

    get transformKilit(): ITransformKilit { return this._transformKilit; }
    set transformKilit(deger: ITransformKilit) {
        this._transformKilit = deger;
        // Kilitleri userData'ya da yansıt (dışarıdan erişim için)
        this.userData['transformKilit'] = deger;
    }

    /** Sürüklenebilir mi (konum değiştirilebilir) */
    get suruklenebilir(): boolean {
        return !this._kilitli && !this._transformKilit.konum;
    }
    set suruklenebilir(deger: boolean) {
        this._transformKilit.konum = !deger;
        this.userData['suruklenebilir'] = deger;
        this.userData['transformKilit'] = this._transformKilit;
    }

    /** Döndürülebilir mi (rotasyon değiştirilebilir) */
    get dondurulebilir(): boolean {
        return !this._kilitli && !this._transformKilit.rotasyon;
    }
    set dondurulebilir(deger: boolean) {
        this._transformKilit.rotasyon = !deger;
        this.userData['dondurulebilir'] = deger;
        this.userData['transformKilit'] = this._transformKilit;
    }

    /** Ölçeklenebilir mi (scale değiştirilebilir) */
    get olceklenebilir(): boolean {
        return !this._kilitli && !this._transformKilit.olcek;
    }
    set olceklenebilir(deger: boolean) {
        this._transformKilit.olcek = !deger;
        this.userData['olceklenebilir'] = deger;
        this.userData['transformKilit'] = this._transformKilit;
    }

    /**
     * TransformControls için izin verilen modları döndürür
     * @returns Kullanılabilir modlar dizisi
     */
    get izinliTransformModlari(): ('translate' | 'rotate' | 'scale')[] {
        const modlar: ('translate' | 'rotate' | 'scale')[] = [];
        if (this.suruklenebilir) modlar.push('translate');
        if (this.dondurulebilir) modlar.push('rotate');
        if (this.olceklenebilir) modlar.push('scale');
        return modlar;
    }

    /**
     * Belirtilen transform modunun izinli olup olmadığını kontrol eder
     */
    transformModuIzinliMi(mod: 'translate' | 'rotate' | 'scale'): boolean {
        switch (mod) {
            case 'translate': return this.suruklenebilir;
            case 'rotate': return this.dondurulebilir;
            case 'scale': return this.olceklenebilir;
            default: return false;
        }
    }

    // ========================================================================
    // TRANSFORM CONTROLS (OTOMATİK)
    // ========================================================================

    /** TransformControls instance */
    private _transformControls: TransformControls | null = null;

    /** TransformControls otomatik eklensin mi */
    private _transformControlsOtomatik: boolean = true;

    get transformControlsOtomatik(): boolean { return this._transformControlsOtomatik; }
    set transformControlsOtomatik(deger: boolean) {
        this._transformControlsOtomatik = deger;
        if (!deger && this._transformControls) {
            this.transformControlsKaldir();
        }
    }

    /** Aktif TransformControls referansı */
    get transformControls(): TransformControls | null {
        return this._transformControls;
    }

    /**
     * TransformControls oluştur ve attach et
     */
    transformControlsOlustur(): void {
        if (!this.sahne?.anaGosterim) return;
        if (this._transformControls) return; // Zaten var
        if (this.izinliTransformModlari.length === 0) return; // Hiçbir mod izinli değil

        const anaGosterim = this.sahne.anaGosterim as any;
        const kamera = anaGosterim.aktifKamera;
        const domElement = anaGosterim.renderer?.domElement ||
                          anaGosterim.webglRenderer?.domElement ||
                          anaGosterim.canvasRef?.nativeElement;

        if (!kamera || !domElement) return;

        // TransformControls oluştur
        this._transformControls = new TransformControls(kamera, domElement);

        // İlk izinli modu ayarla
        const ilkMod = this.izinliTransformModlari[0];
        this._transformControls.setMode(ilkMod);

        // Elementi attach et
        this._transformControls.attach(this);

        // Sahneye ekle
        this.sahne.add(this._transformControls as any);

        // Event listener'lar
        this._transformControls.addEventListener('dragging-changed', (event: any) => {
            // Orbit controls'u devre dışı bırak/aç
            const controls = anaGosterim.orbitControls || anaGosterim.controls || anaGosterim.kameraKontrolleri;
            if (controls) {
                controls.enabled = !event.value;
            }

            if (event.value) {
                this.olaylar.suruklemeBasladi?.();
            } else {
                this.olaylar.suruklemeBitti?.();
                this.olaylar.degisti?.();
            }
        });

        // Mod değişikliğinde izin kontrolü
        this._transformControls.addEventListener('change', () => {
            // Render tetikle
            anaGosterim.ciz?.();
        });
    }

    /**
     * TransformControls'u kaldır
     */
    transformControlsKaldir(): void {
        if (!this._transformControls) return;

        this._transformControls.detach();
        this.sahne?.remove(this._transformControls as any);
        this._transformControls.dispose();
        this._transformControls = null;
    }

    /**
     * TransformControls modunu değiştir
     */
    transformModuDegistir(mod: 'translate' | 'rotate' | 'scale'): boolean {
        if (!this._transformControls) return false;
        if (!this.transformModuIzinliMi(mod)) return false;

        this._transformControls.setMode(mod);
        return true;
    }

    /**
     * Sonraki izinli moda geç
     */
    sonrakiTransformModu(): void {
        if (!this._transformControls) return;

        const modlar = this.izinliTransformModlari;
        if (modlar.length === 0) return;

        const mevcutMod = this._transformControls.mode as 'translate' | 'rotate' | 'scale';
        const mevcutIndex = modlar.indexOf(mevcutMod);
        const sonrakiIndex = (mevcutIndex + 1) % modlar.length;

        this._transformControls.setMode(modlar[sonrakiIndex]);
    }

    // ========================================================================
    // GÖRÜNÜM
    // ========================================================================

    /** Ürün ağacında görünürlük */
    urunAgacindaGorunur: boolean = true;

    /** Çizimde görünürlük */
    cizimdeGorunur: boolean = true;

    /** Render edilsin mi (false ise sahneye eklenmez/çizilmez) */
    private _renderEt: boolean = true;
    get renderEt(): boolean { return this._renderEt; }
    set renderEt(deger: boolean) {
        this._renderEt = deger;
        // visible ile senkronize et ama ayrı bir flag olarak tut
        // renderEt false ise obje tamamen render döngüsünden çıkar
        this.traverse((child) => {
            child.visible = deger ? this.visible : false;
            // frustumCulled'ı da ayarla - renderEt false ise culling'e gerek yok
            child.frustumCulled = deger;
        });
        // Ana objenin visible'ını da ayarla
        if (!deger) {
            this.visible = false;
        }
    }

    /** En üstte render (depth test kapalı) */
    private _enUstte: boolean = false;
    get enUstte(): boolean { return this._enUstte; }
    set enUstte(deger: boolean) {
        this._enUstte = deger;
        this.traverse((child) => {
            if ((child as THREE.Mesh).material) {
                const mat = (child as THREE.Mesh).material as THREE.Material;
                mat.depthTest = !deger;
                mat.depthWrite = !deger;
                if (deger) {
                    child.renderOrder = 999;
                } else {
                    child.renderOrder = 0;
                }
            }
            // Line2 ve diğer çizgi materyalleri için de
            if ((child as any).material && typeof (child as any).material === 'object') {
                const mat = (child as any).material;
                if (mat.depthTest !== undefined) mat.depthTest = !deger;
                if (mat.depthWrite !== undefined) mat.depthWrite = !deger;
                child.renderOrder = deger ? 999 : 0;
            }
        });
        // Ana objenin renderOrder'ını da ayarla
        this.renderOrder = deger ? 999 : 0;
    }

    /** Opaklık (0-1) */
    private _opaklık: number = 1;
    get opaklık(): number { return this._opaklık; }
    set opaklık(deger: number) {
        this._opaklık = Math.max(0, Math.min(1, deger));
        this.traverse((child) => {
            if ((child as THREE.Mesh).material) {
                const mat = (child as THREE.Mesh).material as THREE.Material;
                mat.opacity = this._opaklık;
                mat.transparent = this._opaklık < 1;
            }
        });
    }

    /** Katman (Three.js layers sistemi) */
    private _katman: number = 0;
    get katman(): number { return this._katman; }
    set katman(deger: number) {
        this._katman = deger;
        this.layers.set(deger);
        this.traverse((child) => {
            child.layers.set(deger);
        });
    }

    // ========================================================================
    // HİYERARŞİ
    // ========================================================================

    /** Üst element referansı */
    ustElement: FikoytKok3DElement | null = null;

    /** Alt elementler listesi */
    altElementler: FikoytKok3DElement[] = [];

    /** Element meta verileri */
    metaVeri: Record<string, any> = {};

    // ========================================================================
    // OLAYLAR
    // ========================================================================

    /** Element olayları */
    olaylar: IElementOlaylari = {};

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(sahne?: FikoSahne, isim?: string) {
        super(sahne, isim);

        this.elementId = this.uuidOlustur();
        this.etiket = isim || 'Yeni Element';

        // Ağaçta görünürlük ayarları
        this.userData['FikoArayuz'].agactaGorunur = true;
        this.userData['FikoArayuz'].urunAgaciGorunur = true;
        this.userData['FikoArayuz'].cizimGorunur = true;
        this.userData['FikoArayuz'].simge = './simgeler/3d-element.svg';
        this.userData['FikoArayuz'].elementTip = this.type;
        this.userData['secilebilir'] = true;
    }

    /**
     * Benzersiz UUID oluşturur
     */
    private uuidOlustur(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /**
     * Alt element ekler
     */
    altElementEkle(element: FikoytKok3DElement): this {
        element.ustElement = this;
        this.altElementler.push(element);
        this.add(element);
        return this;
    }

    /**
     * Alt element çıkarır
     */
    altElementCikar(element: FikoytKok3DElement): this {
        const index = this.altElementler.indexOf(element);
        if (index > -1) {
            this.altElementler.splice(index, 1);
            element.ustElement = null;
            this.remove(element);
        }
        return this;
    }

    /**
     * Tüm alt elementleri getirir (recursive)
     */
    tumAltElementleriGetir(): FikoytKok3DElement[] {
        const sonuc: FikoytKok3DElement[] = [];

        const topla = (element: FikoytKok3DElement) => {
            for (const alt of element.altElementler) {
                sonuc.push(alt);
                topla(alt);
            }
        };

        topla(this);
        return sonuc;
    }

    /**
     * Ürün ağacı için veri yapısı döndürür
     */
    urunAgaciVerisi(): any {
        return {
            id: this.elementId,
            isim: this.name,
            etiket: this.etiket,
            tip: this.type,
            aciklama: this.aciklama,
            gorunur: this.visible,
            simge: this.userData['FikoArayuz']?.simge,
            altlar: this.altElementler
                .filter(e => e.urunAgacindaGorunur)
                .map(e => e.urunAgaciVerisi())
        };
    }

    /**
     * Çizim için veri yapısı döndürür
     */
    cizimVerisi(): any {
        return {
            id: this.elementId,
            isim: this.name,
            tip: this.type,
            konum: this.position.clone(),
            rotasyon: this.rotation.clone(),
            olcek: this.scale.clone(),
            gorunur: this.cizimdeGorunur && this.visible,
            metaVeri: this.metaVeri
        };
    }

    // ========================================================================
    // SEÇİM VE VURGULAMA METODLARI
    // ========================================================================

    /**
     * Elementi seç
     */
    sec(): this {
        if (!this._secilebilir || this._kilitli) return this;

        this.secili = true;
        this.durum = 'secili';
        this.secildiginde();
        this.olaylar.secildi?.();
        return this;
    }

    /**
     * Seçimi kaldır
     */
    secimiKaldir(): this {
        this.secili = false;
        this.durum = 'normal';
        this.secimKaldirildiginda();
        this.olaylar.secimKaldirildi?.();
        return this;
    }

    /**
     * Elementi vurgula (hover)
     */
    vurgula(): this {
        if (!this._secilebilir) return this;

        this.vurgulu = true;
        if (!this.secili) {
            this.durum = 'vurgulu';
        }
        this.vurgulandiginda();
        this.olaylar.vurgulandi?.();
        return this;
    }

    /**
     * Vurguyu kaldır
     */
    vurguyuKaldir(): this {
        this.vurgulu = false;
        if (!this.secili) {
            this.durum = 'normal';
        }
        this.vurguKaldirildiginda();
        this.olaylar.vurguKaldirildi?.();
        return this;
    }

    /**
     * Element seçildiğinde çağrılır (override için)
     */
    secildiginde(): void {
        // TransformControls otomatik ekle
        if (this._transformControlsOtomatik && this.izinliTransformModlari.length > 0) {
            this.transformControlsOlustur();
        }
        // Alt sınıflar override edebilir
    }

    /**
     * Element seçimi kaldırıldığında çağrılır (override için)
     */
    secimKaldirildiginda(): void {
        // TransformControls kaldır
        if (this._transformControlsOtomatik) {
            this.transformControlsKaldir();
        }
        // Alt sınıflar override edebilir
    }

    /**
     * Element vurgulandığında çağrılır (override için)
     */
    vurgulandiginda(): void {
        // Alt sınıflar override edebilir
    }

    /**
     * Element vurgusu kaldırıldığında çağrılır (override için)
     */
    vurguKaldirildiginda(): void {
        // Alt sınıflar override edebilir
    }

    // ========================================================================
    // SABİT BOYUT GÜNCELLEME
    // ========================================================================

    /**
     * Sabit boyutu güncelle (her frame çağrılmalı)
     * @param kamera Aktif kamera
     */
    sabitBoyutuGuncelle(kamera: THREE.Camera): void {
        if (!this._sabitBoyut) return;

        const originalScale = this.userData['originalScale'] as THREE.Vector3;
        if (!originalScale) return;

        // Kameraya olan mesafeyi hesapla
        const mesafe = this.position.distanceTo(kamera.position);

        // Ölçek faktörü hesapla
        const faktor = (mesafe / this.sabitBoyutMesafe) * this.sabitBoyutCarpan;

        // Yeni scale uygula
        this.scale.copy(originalScale).multiplyScalar(faktor);
    }

    /**
     * Element güncellendiğinde çağrılır
     * @param deltaTime Delta zaman (saniye)
     * @param kamera Aktif kamera (sabit boyut için)
     */
    guncelle(deltaTime?: number, kamera?: THREE.Camera): void {
        // Sabit boyut güncelle
        if (kamera && this._sabitBoyut) {
            this.sabitBoyutuGuncelle(kamera);
        }

        // Alt sınıflar override edebilir
    }

    // ========================================================================
    // TRANSFORM METODLARI (KİLİT KONTROLLÜ)
    // ========================================================================

    /**
     * Konumu ayarla (kilit kontrolü ile)
     */
    konumAyarla(x: number, y: number, z: number): this {
        if (this._kilitli || this.transformKilit.konum) return this;
        this.position.set(x, y, z);
        this.olaylar.degisti?.();
        return this;
    }

    /**
     * Rotasyonu ayarla (kilit kontrolü ile)
     */
    rotasyonAyarla(x: number, y: number, z: number): this {
        if (this._kilitli || this.transformKilit.rotasyon) return this;
        this.rotation.set(x, y, z);
        this.olaylar.degisti?.();
        return this;
    }

    /**
     * Ölçeği ayarla (kilit kontrolü ile)
     */
    olcekAyarla(x: number, y: number, z: number): this {
        if (this._kilitli || this.transformKilit.olcek) return this;
        this.scale.set(x, y, z);
        this.olaylar.degisti?.();
        return this;
    }

    // ========================================================================
    // GÖRÜNÜM METODLARI
    // ========================================================================

    /**
     * Elementi göster
     */
    goster(): this {
        this.visible = true;
        return this;
    }

    /**
     * Elementi gizle
     */
    gizle(): this {
        this.visible = false;
        return this;
    }

    /**
     * Görünürlüğü değiştir
     */
    gorunurluguDegistir(): this {
        this.visible = !this.visible;
        return this;
    }

    /**
     * Elementi deaktif et
     */
    deaktifEt(): this {
        this.durum = 'deaktif';
        this.opaklık = 0.3;
        this.secilebilir = false;
        return this;
    }

    /**
     * Elementi aktif et
     */
    aktifEt(): this {
        this.durum = 'normal';
        this.opaklık = 1;
        this.secilebilir = true;
        return this;
    }

    // ========================================================================
    // BOUNDING BOX
    // ========================================================================

    /**
     * Bounding box hesapla
     */
    boundingBoxHesapla(): THREE.Box3 {
        const box = new THREE.Box3();
        box.setFromObject(this);
        return box;
    }

    /**
     * Bounding sphere hesapla
     */
    boundingSphereHesapla(): THREE.Sphere {
        const box = this.boundingBoxHesapla();
        const sphere = new THREE.Sphere();
        box.getBoundingSphere(sphere);
        return sphere;
    }

    /**
     * Merkez noktasını al
     */
    merkezNoktasiAl(): THREE.Vector3 {
        const box = this.boundingBoxHesapla();
        const center = new THREE.Vector3();
        box.getCenter(center);
        return center;
    }

    // ========================================================================
    // YAŞAM DÖNGÜSÜ
    // ========================================================================

    /**
     * Element temizlenirken çağrılır
     */
    temizle(): void {
        // TransformControls kaldır
        this.transformControlsKaldir();

        // Olayları temizle
        this.olaylar = {};

        // Alt elementleri temizle
        this.altElementler.forEach(e => e.temizle());
        this.altElementler = [];
        this.ustElement = null;
        this.metaVeri = {};

        // Raycast'i geri yükle
        if (this._originalRaycast) {
            this.raycast = this._originalRaycast;
            this._originalRaycast = null;
        }
    }

    /**
     * Elementi klonla
     */
    klonla(): FikoytKok3DElement {
        const klon = new (this.constructor as any)(this.sahne, `${this.name}_klon`);

        // Temel özellikleri kopyala
        klon.position.copy(this.position);
        klon.rotation.copy(this.rotation);
        klon.scale.copy(this.scale);

        // Ayarları kopyala
        klon._secilebilir = this._secilebilir;
        klon._raycastAktif = this._raycastAktif;
        klon._sabitBoyut = this._sabitBoyut;
        klon.sabitBoyutMesafe = this.sabitBoyutMesafe;
        klon.sabitBoyutCarpan = this.sabitBoyutCarpan;
        klon._kilitli = this._kilitli;
        klon._transformKilit = { ...this._transformKilit };
        klon._renderEt = this._renderEt;
        klon._enUstte = this._enUstte;
        klon._opaklık = this._opaklık;
        klon._katman = this._katman;

        klon.etiket = this.etiket;
        klon.aciklama = this.aciklama;
        klon.metaVeri = { ...this.metaVeri };

        return klon;
    }

    // ========================================================================
    // SERİLEŞTİRME
    // ========================================================================

    /**
     * JSON serileştirme
     */
    toJSON(): any {
        return {
            ...super.toJSON(),
            elementId: this.elementId,
            etiket: this.etiket,
            aciklama: this.aciklama,

            // Seçim ve raycast
            secilebilir: this._secilebilir,
            raycastAktif: this._raycastAktif,

            // Sabit boyut
            sabitBoyut: this._sabitBoyut,
            sabitBoyutMesafe: this.sabitBoyutMesafe,
            sabitBoyutCarpan: this.sabitBoyutCarpan,

            // Kilit ve transform izinleri
            kilitli: this._kilitli,
            transformKilit: this._transformKilit,

            // Görünüm
            urunAgacindaGorunur: this.urunAgacindaGorunur,
            cizimdeGorunur: this.cizimdeGorunur,
            renderEt: this._renderEt,
            enUstte: this._enUstte,
            opaklık: this._opaklık,
            katman: this._katman,

            // Hiyerarşi
            metaVeri: this.metaVeri,
            altElementler: this.altElementler.map(e => e.elementId)
        };
    }

    /**
     * JSON'dan yükle
     */
    fromJSON(json: any): this {
        if (json.elementId) this.elementId = json.elementId;
        if (json.etiket) this.etiket = json.etiket;
        if (json.aciklama) this.aciklama = json.aciklama;

        if (json.secilebilir !== undefined) this.secilebilir = json.secilebilir;
        if (json.raycastAktif !== undefined) this.raycastAktif = json.raycastAktif;

        if (json.sabitBoyut !== undefined) this.sabitBoyut = json.sabitBoyut;
        if (json.sabitBoyutMesafe !== undefined) this.sabitBoyutMesafe = json.sabitBoyutMesafe;
        if (json.sabitBoyutCarpan !== undefined) this.sabitBoyutCarpan = json.sabitBoyutCarpan;

        if (json.kilitli !== undefined) this.kilitli = json.kilitli;
        if (json.transformKilit) this._transformKilit = json.transformKilit;

        if (json.urunAgacindaGorunur !== undefined) this.urunAgacindaGorunur = json.urunAgacindaGorunur;
        if (json.cizimdeGorunur !== undefined) this.cizimdeGorunur = json.cizimdeGorunur;
        if (json.renderEt !== undefined) this.renderEt = json.renderEt;
        if (json.enUstte !== undefined) this.enUstte = json.enUstte;
        if (json.opaklık !== undefined) this.opaklık = json.opaklık;
        if (json.katman !== undefined) this.katman = json.katman;

        if (json.metaVeri) this.metaVeri = json.metaVeri;

        return this;
    }
}
